"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { EntityTitle, EntitySubtitle } from "./EntityTitle";
import { Badge } from "@/components/ui/data-display/badge";

type Level = "hero" | "page" | "section" | "subsection";

export type SectionHeaderProps = {
  /**
   * Visual level (matches home-page language):
   *   hero       — full page hero (text-hero, responsive 24→48px extrabold)
   *   page       — view-level heading (text-title-lg)
   *   section    — tactical uppercase section label (text-section, 13px extrabold 0.14em)
   *   subsection — smaller uppercase eyebrow (text-overline, 10px 0.18em)
   */
  level?: Level;
  /** Small uppercase kicker rendered above the title (hero/page only). */
  kicker?: React.ReactNode;
  title: React.ReactNode;
  icon?: LucideIcon;
  /** Count rendered beside the title as a small tabular number. */
  count?: number | string;
  /** Description / subtitle below the title. */
  description?: React.ReactNode;
  /** Elements rendered at the far right (links, buttons, toggles). */
  trailing?: React.ReactNode;
  /** Vertical alignment between title block and trailing controls. */
  align?: "start" | "end";
  /** Optional override for title typography when a stronger header is needed. */
  titleClassName?: string;
  /** Override description typography — useful when the default level size is too large for the context (e.g. a dialog). */
  descriptionClassName?: string;
  className?: string;
};

/**
 * `hero` and `subsection` keep their legacy tokens (hero is huge, subsection
 * is a small-uppercase eyebrow). `page` and `section` delegate to
 * EntityTitle so section-level headers across the app stay visually
 * consistent with entity list-item titles.
 */
const TITLE_CLASS: Record<Level, string> = {
  hero: "text-hero text-foreground",
  page: "",
  section: "",
  subsection: "text-overline text-foreground",
};

const ENTITY_TITLE_SIZE: Partial<Record<Level, "sm" | "md" | "lg">> = {
  page: "lg",
  section: "sm",
};

const ICON_CLASS: Record<Level, string> = {
  hero: "h-5 w-5",
  page: "h-5 w-5",
  section: "h-4 w-4",
  subsection: "h-3.5 w-3.5",
};

const GAP_CLASS: Record<Level, string> = {
  hero: "gap-3",
  page: "gap-3",
  section: "gap-2",
  subsection: "gap-1.5",
};

export function SectionHeader({
  level = "section",
  kicker,
  title,
  icon: Icon,
  count,
  description,
  trailing,
  align = "start",
  titleClassName,
  descriptionClassName,
  className,
}: SectionHeaderProps) {
  const isTactical = level === "section" || level === "subsection";
  return (
    <div
      className={cn(
        "flex justify-between gap-4",
        align === "end" ? "items-end" : "items-start",
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        {kicker ? (
          <p className="text-overline text-primary mb-1">{kicker}</p>
        ) : null}
        <div className={cn("flex items-center", GAP_CLASS[level])}>
          {Icon ? (
            <Icon
              className={cn(
                ICON_CLASS[level],
                isTactical
                  ? "text-primary shrink-0"
                  : "text-muted-foreground shrink-0",
              )}
              aria-hidden="true"
            />
          ) : null}
          {ENTITY_TITLE_SIZE[level] ? (
            <EntityTitle
              as="h2"
              size={ENTITY_TITLE_SIZE[level]!}
              className={cn("min-w-0 break-words", titleClassName)}
            >
              {title}
            </EntityTitle>
          ) : (
            <h2
              className={cn(
                TITLE_CLASS[level],
                "min-w-0 break-words",
                titleClassName,
              )}
            >
              {title}
            </h2>
          )}
          {count !== undefined && count !== null && (
            <Badge variant="outline" className="tabular-nums">
              {count}
            </Badge>
          )}
        </div>
        {description
          ? level === "hero" || level === "page"
            ? (
              <p
                className={cn(
                  "mt-1 text-body-lg text-muted-foreground max-w-2xl",
                  descriptionClassName,
                )}
              >
                {description}
              </p>
            )
            : (
              <EntitySubtitle
                as="p"
                tone="muted"
                className={cn("mt-1 text-caption", descriptionClassName)}
              >
                {description}
              </EntitySubtitle>
            )
          : null}
      </div>
      {trailing ? (
        <div className="flex items-center gap-2 shrink-0">{trailing}</div>
      ) : null}
    </div>
  );
}
